<template>
  <div v-if="hasChecksForDisplay" class="tw-flex tw-flex-col tw-gap-4">
    <SfFlash v-if="error" type="alert" :message="tryAgainText" />

    <template v-for="check in targetChecks">
      <template v-if="shouldRenderCheck(check)">
        <SfAssistantCheck
          v-if="!onlyTodos || remarks[check.id]"
          :key="check.id"
          :check="check"
          :dimmed="
            isAnythingHighlighted && String(check.id) !== highlightedAssistant
          "
          :highlighted="String(check.id) === highlightedAssistant"
          :read="checkReadStatus[check.id]"
          :read-by-users="checkReadByUsers[check.id]"
          :remark="remarks[check.id]"
          :show-action="true"
          @add-todo="addTodo"
          @clicked-outside="handleClickOutside"
          @clear-highlight="clearHighlight"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import type { Check } from '../types'
import type { Remark } from '@app/application/communication/types'

import { storeToRefs } from 'pinia'
import { onMounted, computed } from 'vue'

import SfAssistantCheck from './sf-assistant-check.vue'
import { RightDrawerContent } from '@app/shared/types/drawer'
import SfFlash from '@app/shared/vue/components/sf-flash.vue'

import useSfAssistantChecks from '@app/application/sf-assistant-checks/composables/use-sf-assistant-checks'
import {
  doneChecks,
  pendingChecks,
} from '@app/application/sf-assistant-checks/utils'

import { useChecksStore } from '@app/application/sf-assistant-checks/stores/checks'
import { useDrawer } from '@app/shared/pinia/drawer'
import { useHighlightedCard } from '@app/shared/pinia/highlighted-card'
import { useAssistantChecksStore } from '@app/application/sf-assistant-checks/stores/assistant-checks'

interface Props {
  remarks: Record<string, Remark>
  expanded?: boolean
  onlyTodos?: boolean
  done?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  expanded: false,
  onlyTodos: false,
  done: false,
})

const highlightedCardStore = useHighlightedCard()
const { highlightedCard } = storeToRefs(highlightedCardStore)

const drawerStore = useDrawer()
const assistantChecksStore = useAssistantChecksStore()
const { checkReadStatus, checkReadByUsers } = storeToRefs(assistantChecksStore)

onMounted(() => {
  assistantChecksStore.initFromChecksStore()

  const anchor = window.location.hash.replace('#', '')
  const assistantIdRegex = /assistant-(.*)/
  const match = anchor.match(assistantIdRegex)

  if (match && match[1]) {
    drawerStore.openRightDrawer(RightDrawerContent.Communication, true)
    highlightedCardStore.setHighlightedCard({
      id: match[1],
      type: 'assistant',
    })
  }
})

const checksStore = useChecksStore()
useSfAssistantChecks()

const { checks, error, hasChecks } = storeToRefs(checksStore)

// translations
const tryAgainText = window.I18n.t('common.error.try_again')
const addTodoTitle = window.I18n.t(
  'account_entry.assistant.check.add_todo_title'
)
const addTodoSaveButtonText = window.I18n.t(
  'account_entry.assistant.check.add_todo_save_button_label'
)

const isAnythingHighlighted = computed(() => !!highlightedCard.value.id)
const highlightedAssistant = computed(() => {
  if (highlightedCard.value.type !== 'assistant') {
    return null
  }

  return highlightedCard.value.id
})

const hasChecksForDisplay = computed(() => {
  //return false if we have no checks
  if (!hasChecks.value) {
    return false
  }

  //if not in only todos mode return true since there are some checks
  if (!props.onlyTodos) {
    return true
  }

  //depending what kind of todos we are return if checks are present
  return props.done
    ? doneChecks(checks.value, props.remarks).length > 0
    : pendingChecks(checks.value, props.remarks).length > 0
})

const targetChecks = computed(() => checks.value)

function handleClickOutside(highlighted: boolean): void {
  if (highlighted) {
    clearHighlight()
  }
}

function clearHighlight(): void {
  highlightedCardStore.unsetHighlightedCard()
  window.eventHub.$emit('clearAnchor')
}

function shouldRenderCheck(check: Check): boolean {
  if (!props.onlyTodos) {
    return true
  }

  const remark = props.remarks[check.id]
  return remark ? remark.done === props.done : false
}

function addTodo(check: Check): void {
  const ownerType =
    check.accountId !== null && check.accountId !== 0
      ? 'Account'
      : 'ReconciliationText'
  const ownerId =
    ownerType === 'Account' ? check.accountId : check.reconciliationId

  window.modalCitizen.openModal('new-remark', {
    ownerType: ownerType,
    ownerId: ownerId,
    ledgerId: window.Silverfin.currentLedgerId(),
    allRemarkTags: window.allRemarkTags,
    showTags: true,
    type: 'todo',
    hideTypeSelector: true,
    hidePeriodSelector: true,
    title: addTodoTitle,
    saveButtonLabel: addTodoSaveButtonText,
    assistantCheckId: check.id,
  })
}
</script>
